/**
 * decisionBuckets — how far through a review actually is.
 *
 * Three buckets, not two:
 *
 *   decided     accepted / rejected / modified — an answer
 *   escalated   routed to someone else — OPEN
 *   pending     untouched — OPEN
 *
 * Escalated used to fall into `decided`, because every check on the finding's
 * `decision` field was a truthy test and escalated is a truthy value. The
 * progress bar filled, and Complete Review offered itself, with findings
 * nobody had resolved.
 *
 * The backend is being changed to send `escalated` on its decision summary
 * and to stop counting it as decided. Until that lands, the count is derived
 * here from the loaded findings — so this reads correctly both before and
 * after, and the display does not have to know which it got.
 */

import { DecisionProgress, DecisionSummary, Finding, isResolvedDecision } from '../../../types/review';

export interface DecisionBuckets {
  total: number;
  decided: number;
  escalated: number;
  pending: number;
  /** Escalated + pending — everything still needing an answer. */
  unresolved: number;
}

type ProgressLike = Pick<DecisionProgress | DecisionSummary, 'total' | 'decided' | 'pending'> & {
  escalated?: number;
};

/**
 * Prefers the server's numbers, because they cover findings this client has
 * not loaded — a session can have far more findings than the current page.
 *
 * The exception is `escalated` while the API does not send it: an absent
 * count is derived from the loaded findings, and `decided` is corrected down
 * by the same amount, because a server that does not yet report escalated is
 * by definition still counting it inside `decided`.
 */
export function decisionBuckets(
  findings: Finding[],
  progress?: ProgressLike | null
): DecisionBuckets {
  const escalatedLoaded = findings.filter((f) => f.decision === 'escalated').length;

  if (progress && typeof progress.total === 'number') {
    const escalated = typeof progress.escalated === 'number'
      ? progress.escalated
      : escalatedLoaded;

    // Only take escalated back OUT of decided when the server did not
    // already separate it; doing both would subtract it twice.
    const decided = typeof progress.escalated === 'number'
      ? progress.decided
      : Math.max(0, progress.decided - escalated);

    const pending = Math.max(0, progress.total - decided - escalated);
    return {
      total: progress.total,
      decided,
      escalated,
      pending,
      unresolved: escalated + pending,
    };
  }

  // No server numbers yet — everything from what is loaded.
  const decided = findings.filter((f) => isResolvedDecision(f.decision)).length;
  const total = findings.length;
  const pending = Math.max(0, total - decided - escalatedLoaded);
  return {
    total,
    decided,
    escalated: escalatedLoaded,
    pending,
    unresolved: escalatedLoaded + pending,
  };
}

/** Nothing left open, and there was something to begin with. */
export const isFullyResolved = (buckets: DecisionBuckets): boolean =>
  buckets.total > 0 && buckets.unresolved === 0;

export default decisionBuckets;
